import { Track, type TrackParams } from "@/src/lib/tracking";
/**
 * 13.通知按钮列表
 */

import { Icon, OspToast, ospThemes } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSnapshot } from "valtio";
import { buildEdgenAiRankingHref } from "../../EdgenAI/buildEdgenAiRankingHref";
import { useJsonRenderRuntimeContext } from "../jsonRender/JsonRenderRuntimeContext";
import {
  NotificationAction,
  askEdButtonClass,
  investMovesActionsRowClass,
  investMovesAskEdButtonClass,
  notificationNormalClass,
  trackerActionsRowClass,
  trackerAskEdButtonClass,
  trackerDislikeButtonClass,
  trackerViewDetailButtonClass,
} from "./type";
import { buildViewDetailHref } from "./buildViewDetailHref";
import { navigateAfterClosingChat } from "./navigateAfterClosingChat";
import { formatInvestMovesRelativeTime } from "./utils";
import Translations from "@translate/components/Chat/JsonRenderComponents/NotificationButtonList";
import { I18nUtil } from "@utils/i18n";

import {
  trackGoalLiveCardTap,
  trackChatBriefCardTap,
} from "@/src/hooks/onboarding/onboardingAnalytics";
import { ROUTER_PATH } from "@config/router";
import {
  type AddPositionAsset,
  PositionAssetTypeEnum,
} from "@features/api/memberApi";
import { favoritesCenter } from "@store/favorite/FavoritesCenter";
import { rootStore } from "@store/rootStore";
import { useActionLoading } from "@hooks/useActionLoading";
import { useIsPc } from "@hooks/useIsPc";
import { useMemberUser } from "@hooks/useMemberUser";
import { useOpenChatModal } from "@hooks/useOpenChatModal";
import { useShellRouter } from "@hooks/useShellAwareRouter";
import {
  hideNotifyActions,
  isNotifyActionHidden,
} from "@components/Notification";
import { buildOpenReplayLabel } from "@utils/openreplay";
import { AlphaPickPricingModalCallable } from "../../AlphaPick/AlphaPickPricing";
import NextImage from "../../NextImage";

const I18n = I18nUtil.createScoped(Translations);
const DEFAULT_NOTIFICATION_ACTION_LOADING_MS = 3000;
const NOTIFICATION_ACTION_LOADING_MS_BY_CAT_ID: Partial<
  Record<string, number>
> = {
  ask_ed: 3000,
  explore_picks: 3000,
  view_detail: 3000,
  compare_more: 3000,
};

export interface NotificationButtonListProps {
  actions: NotificationAction[];
}

const readString = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

const readStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === "string");
  }
  if (typeof value === "string") {
    return [value];
  }
  return [];
};

const getActionCatIds = (attributes: Record<string, unknown>): string[] => {
  const catIds = [
    ...readStringArray(attributes.catIds),
    ...readStringArray(attributes.cat_ids),
  ];
  if (catIds.length > 0) {
    return Array.from(new Set(catIds.filter((id) => id.length > 0)));
  }

  const catId = readString(attributes.catId) ?? readString(attributes.cat_id);
  return catId ? [catId] : [];
};

const mapWireAssetTypeToPositionEnum = (
  raw: unknown,
): PositionAssetTypeEnum => {
  const s = typeof raw === "string" ? raw.toLowerCase() : "";
  if (s === "crypto" || s === PositionAssetTypeEnum.TOKEN) {
    return PositionAssetTypeEnum.TOKEN;
  }
  return PositionAssetTypeEnum.STOCK;
};

/**
 * Builds API payload from `attributes.assets` (symbol + assetType) or explicit asset_id rows.
 */
const pickAddPositionAssetsFromAttributes = (
  attributes: Record<string, unknown>,
): AddPositionAsset[] => {
  const raw = attributes.assets;
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: AddPositionAsset[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const row = item as Record<string, unknown>;
    const assetType = mapWireAssetTypeToPositionEnum(
      readString(row.assetType) ?? readString(row.asset_type),
    );
    const routeAssetId =
      readString(row.assetRouteId) ?? readString(row.asset_route_id);
    if (routeAssetId) {
      out.push({
        asset_id: routeAssetId,
        asset_type: assetType,
      });
    }
  }
  return out;
};

const parseRankingThemes = (raw: unknown): { id: string; label: string }[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: { id: string; label: string }[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const row = item as Record<string, unknown>;
    const id = readString(row.id);
    const label = readString(row.label);
    if (id && label) {
      out.push({ id, label });
    }
  }
  return out;
};

const buildNotificationCtaOpenReplayLabel = (
  scene: string | undefined,
): string => {
  const sceneLabel =
    scene === "chat"
      ? "Chat"
      : scene === "tracker"
        ? "Tracker"
        : "Notification";
  return buildOpenReplayLabel(sceneLabel, "NotificationButton", "Click");
};

const readMetricsValue = (value: unknown): string | undefined =>
  typeof value === "string" && value.length > 0 ? value : undefined;

const mapNotificationAssetTypeToRanking = (
  raw: unknown,
): "stock" | "etf" | "crypto" => {
  const s = typeof raw === "string" ? raw.toLowerCase() : "";
  if (s === "crypto") {
    return "crypto";
  }
  return "stock";
};

const getNotificationActionLoadingDuration = (catId?: string): number => {
  if (!catId) {
    return DEFAULT_NOTIFICATION_ACTION_LOADING_MS;
  }
  return (
    NOTIFICATION_ACTION_LOADING_MS_BY_CAT_ID[catId] ??
    DEFAULT_NOTIFICATION_ACTION_LOADING_MS
  );
};

export const NotificationButtonList: React.FC<NotificationButtonListProps> = ({
  actions,
}) => {
  const renderContext = useJsonRenderRuntimeContext();
  const router = useShellRouter();
  const handleOpenChatModal = useOpenChatModal();
  const { userFolders } = useSnapshot(favoritesCenter.state);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const folderId = userFolders?.[0]?.id || "";
  const ownerId = ownerInfo?.id;
  const notificationId = renderContext?.notificationId;
  const title = renderContext?.title;
  const currentScene = renderContext?.scene;
  const { data: memberPlanInfo } = useMemberUser();
  const [isPC] = useIsPc();
  const { isLoading, startLoading, withManualLoading } = useActionLoading({
    defaultDurationMs: DEFAULT_NOTIFICATION_ACTION_LOADING_MS,
  });
  const [sessionHiddenCatIds, setSessionHiddenCatIds] = useState<Set<string>>(
    () => new Set(),
  );

  useEffect(() => {
    setSessionHiddenCatIds(new Set());
  }, [notificationId, ownerId]);

  const isTrackerScene = currentScene === "tracker";
  const isInvestNextMovesScene = currentScene === "invest-nextmoves";
  const investMovesTimeLabel =
    isInvestNextMovesScene && renderContext?.generatedAtMs
      ? formatInvestMovesRelativeTime(renderContext.generatedAtMs)
      : "";

  const markActionHidden = useCallback(
    (attributes: Record<string, unknown>) => {
      const catIds = getActionCatIds(attributes);
      if (catIds.length === 0) {
        return;
      }
      // Tracker 详情页不与铃铛页共用 localStorage 隐藏态，避免 Dislike 被误隐藏。这里不对
      hideNotifyActions(ownerId, notificationId, catIds);
      setSessionHiddenCatIds((prev) => {
        const next = new Set(prev);
        catIds.forEach((catId) => {
          next.add(catId);
        });
        return next;
      });
    },
    [notificationId, ownerId],
  );

  const visibleActions = useMemo(() => {
    if (currentScene === "tracker-history") {
      return [];
    }
    return actions.filter((action) => {
      const catIds = getActionCatIds(action.attributes);
      if (catIds.length === 0) {
        return true;
      }

      const hiddenBySession = catIds.some((catId) =>
        sessionHiddenCatIds.has(catId),
      );
      const hiddenByPersisted = catIds.some((catId) =>
        isNotifyActionHidden(ownerId, notificationId, catId),
      );
      return !(hiddenBySession || hiddenByPersisted);
    });
  }, [actions, currentScene, notificationId, ownerId, sessionHiddenCatIds]);

  const hideVisibleNegativeActions = useCallback(() => {
    visibleActions.forEach((visibleAction) => {
      if (visibleAction.type === "negative") {
        markActionHidden(visibleAction.attributes);
      }
    });
  }, [markActionHidden, visibleActions]);

  const handleClick = useCallback(
    async (action: NotificationAction, actionKey: string) => {
      const { type, attributes } = action;
      const actionCatIds = getActionCatIds(attributes);
      const catId = readString(attributes.catId) ?? actionCatIds[0];
      // Ensure negative feedback (especially "skip") is persisted immediately
      // so cross-device sync does not depend on later rerenders/pagination.
      if (type === "negative" || catId === "skip") {
        markActionHidden(attributes);
      }
      // build event params
      const ctaPayload: Record<string, string> = {};
      const sym = readString(attributes.symbol);
      if (sym) {
        ctaPayload.ticker = sym;
      }
      const actionId =
        readString(attributes.action_id) ?? readString(attributes.actionId);
      ctaPayload.content_scene = currentScene ?? "notification";
      ctaPayload.cta_id = catId;
      ctaPayload.type = type;
      // 类型是 `TrackParams`（只收标量）而非 `Record<string, unknown>`：规范 §3.2「标量优先，
      // 扁平比嵌套好查」。这里每个值本来都是标量（`readMetricsValue` 返回 `string | undefined`），
      // 收窄类型只是把这个约束写进签名，让以后塞对象进来时当场编译失败。
      const openReplayPayload: TrackParams = {
        ...ctaPayload,
        label: readString(attributes.label),
        notificationId,
        action_id: actionId,
      };
      const metrics = readMetricsValue(renderContext?.metrics);
      if (metrics !== undefined) {
        openReplayPayload.metrics = metrics;
      }
      const eventType = readString(attributes.event_type);
      if (eventType) {
        // ⚠️ 业务键 `event_type`（信号类型，如 earnings）必须改名 `signal_event_type` ——
        // envelope 自己会注入 `event_type`（view/action/click/log/error），而 `envelope()`
        // 是先 spread 业务参数再写 envelope 键，同名业务值被**无条件静默覆盖**。
        // `scene` 那个是条件覆盖（有 runtime scene 才覆盖），这个是每次都覆盖，更隐蔽。
        // 两端同步：native `ChatNotificationCards.kt` / `BriefScreen.kt` 同一天改成了同样的键名。
        openReplayPayload.signal_event_type = eventType;
      }
      // 走 `Track.event` 而非裸调 `emitOpenReplayEvent`：拿到 envelope（规范 §4.3）。
      // ⚠️ 事件名与 `notificationId` 都**不许动** —— 下游 settler 直接读
      // `$properties.notificationId` 做曝光↔反馈配对；`notificationId` 是 camelCase，
      // 违反规范 §3.2，但**因下游依赖而冻结**，不许顺手清理。
      Track.event("notification_button_feedback", openReplayPayload);

      if (type === "positive") {
        Track.event("signal_cta_tap", ctaPayload);
        if (catId === "add_holding") {
          await withManualLoading(actionKey, async () => {
            const positionAssets =
              pickAddPositionAssetsFromAttributes(attributes);
            const result = await favoritesCenter.batchAddAssets(
              positionAssets,
              folderId,
            );
            const toastDup =
              readString(attributes.toastAlreadyExists) ??
              I18n.t("Add holdings failed");
            if (result.success) {
              markActionHidden(attributes);
              hideVisibleNegativeActions();
              return;
            }
            OspToast.error({
              content: toastDup,
            });
          });
          return;
        }

        startLoading(actionKey, {
          durationMs: getNotificationActionLoadingDuration(catId),
        });
        if (catId === "ask_ed") {
          const question =
            readString(attributes.question) ??
            I18n.t("What should I know about this update?");
          // proactive_notification 引用已废弃（后端 2026-06 移除，无 resolver，命中 unknown
          // fail-safe）。通知追问以纯文本提问 — 后续如需恢复通知上下文，须先在后端
          // registry 注册 resolver，再重新拼协议行。
          void handleOpenChatModal({
            question,
            autoSubmit: false,
          });
        } else if (catId === "explore_picks") {
          navigateAfterClosingChat(router, ROUTER_PATH.INVEST_OPPORTUNITY);
        } else if (catId === "view_detail") {
          const href = buildViewDetailHref(attributes);
          if (href) {
            navigateAfterClosingChat(router, href);
          }
        } else if (catId === "subscribe") {
          // 吊起 paywall
          await AlphaPickPricingModalCallable({
            isPC,
            trackingModuleName: "edgen_notification_active",
            paywallCopyOverrides: {
              title: I18n.t(
                "Someone should always be watching the market for you",
              ),
              subtitle: I18n.t("Ed already is."),
              features: [
                I18n.t("Track your holdings while you're away"),
                I18n.t("Flag risks before they hit"),
                I18n.t("Full read on all stocks"),
              ],
              hideSocialProof: true,
            },
          });
        } else if (catId === "view_plans") {
          navigateAfterClosingChat(router, ROUTER_PATH.MEMBER_SUBSCRIPTION);
        } else if (catId === "navigate_goal") {
          trackGoalLiveCardTap();
          navigateAfterClosingChat(router, ROUTER_PATH.PLAN_MY_GOALS);
        } else if (catId === "navigate_brief") {
          trackChatBriefCardTap();
          navigateAfterClosingChat(router, ROUTER_PATH.BRIEF);
        } else if (catId === "compare_more") {
          // not member should purchase ed
          if (!memberPlanInfo?.isMember) {
            await AlphaPickPricingModalCallable({
              isPC,
              trackingModuleName: "edgen_notification_active",
              paywallCopyOverrides: {
                title: I18n.t(
                  "Someone should always be watching the market for you",
                ),
                subtitle: I18n.t("Ed already is."),
                features: [
                  I18n.t("Track your holdings while you're away"),
                  I18n.t("Flag risks before they hit"),
                  I18n.t("Full read on all stocks"),
                ],
                hideSocialProof: true,
              },
            });
          } else {
            const symbol = readString(attributes.symbol);
            const themes = parseRankingThemes(attributes.themes);
            const assetType = mapNotificationAssetTypeToRanking(
              attributes.assetType,
            );
            const rankingId =
              readString(attributes.assetRouteId) ??
              readString(attributes.asset_route_id) ??
              readString(attributes.id);
            const initialThemeId =
              readString(attributes.initialThemeId) ?? themes[0]?.id;
            // 与资产详情页路由契约对齐：
            // - stock / etf 依赖 symbol
            // - crypto 优先使用详情页同源的 route id；symbol 仅作为页面内兼容兜底
            const rankingPath = buildEdgenAiRankingHref({
              assetType,
              id: assetType === "crypto" ? (rankingId ?? "") : "",
              symbol: symbol ?? "",
              initialThemeId,
            });
            if (rankingPath) {
              navigateAfterClosingChat(router, rankingPath);
            }
          }
        }
        hideVisibleNegativeActions();
        return;
      }
      // build negative event params
      ctaPayload.cta_id = catId;
      Track.event("signal_not_interested_tap", ctaPayload);
      OspToast.success({
        content: I18n.t("Ed got your feedback."),
      });
    },
    [
      currentScene,
      notificationId,
      renderContext?.metrics,
      markActionHidden,
      startLoading,
      hideVisibleNegativeActions,
      withManualLoading,
      folderId,
      title,
      handleOpenChatModal,
      router,
      memberPlanInfo?.isMember,
      isPC,
    ],
  );
  if (visibleActions.length === 0) {
    return null;
  }

  const resolvePositiveButtonClass = (catId?: string) => {
    if (isInvestNextMovesScene) {
      return investMovesAskEdButtonClass;
    }
    if (isTrackerScene) {
      if (catId === "ask_ed") {
        return trackerAskEdButtonClass;
      }
      if (catId === "view_detail") {
        return trackerViewDetailButtonClass;
      }
    }
    if (catId === "ask_ed" || catId === "subscribe") {
      return askEdButtonClass;
    }
    return notificationNormalClass;
  };

  return (
    <div
      className={cn(
        isInvestNextMovesScene
          ? investMovesActionsRowClass
          : isTrackerScene
            ? trackerActionsRowClass
            : "flex flex-wrap gap-3",
      )}
      onClick={
        isInvestNextMovesScene ? (event) => event.stopPropagation() : undefined
      }
    >
      <div
        className={cn(
          isInvestNextMovesScene ? "flex flex-wrap gap-3" : "contents",
        )}
      >
        {visibleActions.map((item, index) => {
          const tone = item.type ?? "negative";
          const label = readString(item.attributes.label) ?? "";
          const catId =
            readString(item.attributes.catId) ??
            getActionCatIds(item.attributes)[0];
          const domLabel = buildNotificationCtaOpenReplayLabel(
            renderContext?.scene,
          );
          const actionKey = `${catId ?? "unknown"}-${index}`;
          const actionIsLoading = isLoading(actionKey);

          if (tone === "positive") {
            const isTrackerAskEd = isTrackerScene && catId === "ask_ed";
            const isTrackerViewDetail =
              isTrackerScene && catId === "view_detail";
            const isInvestMovesAskEd =
              isInvestNextMovesScene && catId === "ask_ed";
            const loadingIconColor = isTrackerAskEd
              ? ospThemes.colors.content_secondary
              : isInvestMovesAskEd
                ? ospThemes.colors.content_brand_primary
                : catId === "ask_ed"
                  ? ospThemes.colors.content_inverse_primary
                  : ospThemes.colors.content_primary;

            return (
              <button
                key={`${label}-${index}`}
                type="button"
                className={cn(
                  resolvePositiveButtonClass(catId),
                  "group min-w-[76px] w-fit",
                )}
                data-testid={
                  isTrackerAskEd ? "desk-detail-signal-ask-ed-click" : undefined
                }
                data-openreplay-label={domLabel}
                data-loading={actionIsLoading}
                data-disabled={actionIsLoading}
                disabled={actionIsLoading}
                onClick={() => {
                  if (actionIsLoading) {
                    return;
                  }
                  void handleClick(item, actionKey);
                }}
              >
                <span
                  className={cn(
                    "group-loading:hidden inline-flex items-center gap-0.5",
                    isTrackerViewDetail && "text-content_inverse_primary",
                    isTrackerAskEd && "text-content_secondary",
                    isInvestMovesAskEd && "text-content_brand_primary",
                    !isTrackerScene &&
                      !isInvestNextMovesScene &&
                      (catId === "ask_ed"
                        ? "text-content_inverse_primary"
                        : "text-content_primary"),
                  )}
                >
                  {isTrackerAskEd && (
                    <Icon
                      name="p_chat"
                      size={20}
                      color={ospThemes.colors.content_secondary}
                    />
                  )}
                  <span
                    className={cn(
                      isTrackerScene || isInvestNextMovesScene
                        ? "button_m whitespace-nowrap"
                        : "button_s",
                      isTrackerAskEd && "text-content_secondary",
                      isInvestMovesAskEd &&
                        "button_s text-content_brand_primary",
                      isTrackerViewDetail && "text-content_inverse_primary",
                    )}
                  >
                    {label}
                  </span>
                  {(isTrackerViewDetail ||
                    isInvestMovesAskEd ||
                    (!isTrackerScene &&
                      !isInvestNextMovesScene &&
                      catId === "ask_ed")) && (
                    <Icon
                      name="right-arrow-alt"
                      size={isTrackerScene ? 20 : 16}
                      color={
                        isInvestMovesAskEd
                          ? ospThemes.colors.content_brand_primary
                          : isTrackerViewDetail
                            ? ospThemes.colors.content_inverse_primary
                            : ospThemes.colors.content_inverse_primary
                      }
                    />
                  )}
                </span>
                <Icon
                  name="ic_loading2"
                  size={16}
                  color={loadingIconColor}
                  className={cn("hidden animate-spin group-loading:block")}
                />
              </button>
            );
          }
          return (
            <button
              key={`${label}-${index}`}
              type="button"
              className={cn(
                isTrackerScene
                  ? cn(trackerDislikeButtonClass, "justify-self-end")
                  : notificationNormalClass,
              )}
              aria-label={isTrackerScene ? "Dislike" : undefined}
              data-testid={
                isTrackerScene && item.type === "negative"
                  ? "desk-detail-signal-dislike-click"
                  : undefined
              }
              data-openreplay-label={domLabel}
              onClick={() => void handleClick(item, actionKey)}
            >
              {item.type === "negative" && (
                <NextImage
                  src="/assets/images/dislike.svg"
                  width={16}
                  height={16}
                  alt=""
                  className="shrink-0"
                  loading="eager"
                />
              )}
              {!(isTrackerScene && item.type === "negative") && (
                <span className="button_s text-content_primary">{label}</span>
              )}
            </button>
          );
        })}
      </div>
      {isInvestNextMovesScene && investMovesTimeLabel ? (
        <span className="body_s shrink-0 whitespace-nowrap text-content_tertiary">
          {investMovesTimeLabel}
        </span>
      ) : null}
    </div>
  );
};
